import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Hero from "../components/Hero";
import Seo from "../components/Seo";
import NewsCard from "../components/NewsCard";
import Reveal from "../components/Reveal";
import AdSlot from "../components/AdSlot";
import SupportUs from "../components/SupportUs";
import { useLang, SECTIONS, pickTitle } from "../lib/i18n";

function SectionRail({ section, articles }) {
  const { lang, t } = useLang();
  if (!articles?.length) return null;
  const [lead, ...rest] = articles;
  return (
    <Reveal className="border-t-2 border-ink pt-7">
      <section data-testid={`section-${section.id}`}>
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-hindi text-2xl md:text-3xl text-ink">
            <Link
              to={`/section/${section.id}`}
              data-testid={`section-title-${section.id}`}
              className="hover:text-deepgreen transition-colors"
            >
              {lang === "hi" ? section.hi : section.en}
            </Link>
            <span className="block gold-rule w-24 mt-2" />
          </h2>
          <Link
            to={`/section/${section.id}`}
            data-testid={`section-more-${section.id}`}
            className="text-xs font-mono uppercase tracking-[0.18em] text-brk hover:text-deepgreen transition-colors"
          >
            {t.seeAll} →
          </Link>
        </div>
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <NewsCard article={lead} variant="standard" />
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-x-8">
            {rest.slice(0, 4).map((a) => (
              <NewsCard key={a.id} article={a} variant="list" />
            ))}
          </div>
        </div>
      </section>
    </Reveal>
  );
}

export default function HomePage() {
  const [data, setData] = useState(null);
  const [ads, setAds] = useState([]);
  const { lang, t } = useLang();

  useEffect(() => {
    let stop = false;
    const load = () =>
      api
        .get("/articles/home")
        .then((r) => !stop && setData(r.data))
        .catch(() => !stop && setData((prev) => prev || { error: true }));
    load();
    const iv = setInterval(load, 60000);
    api
      .get("/ads")
      .then((r) => setAds(r.data.ads))
      .catch(() => {});
    return () => {
      stop = true;
      clearInterval(iv);
    };
  }, []);

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center font-mono text-sm text-muted-foreground" data-testid="home-loading">
        …
      </div>
    );
  }
  if (data.error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="home-error">
        {t.loadError}
      </div>
    );
  }

  const railOrder = ["rajasthan", "politics", "national", "international", "business", "sports", "entertainment", "tech", "lifestyle", "viral"];

  // Homepage dedupe: hero → top → latest → trending → rails; har khabar sirf ek baar
  const used = new Set();
  const take = (list, n) => {
    const out = [];
    for (const a of list || []) {
      if (out.length >= n) break;
      if (!used.has(a.id)) {
        used.add(a.id);
        out.push(a);
      }
    }
    return out;
  };
  const hero = data.hero;
  if (hero) used.add(hero.id);
  const top = take(data.top, 4);
  const latest = take(data.latest, 8);
  const trending = take(
    [...data.trending].filter((a) => !used.has(a.id)),
    6
  );
  const opinions = take(data.opinions, 4);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-14" data-testid="home-page">
      <Seo path="/" />
      <Hero hero={hero} top={top} />

      <AdSlot slot="top" ads={ads} />

      <Reveal className="grid lg:grid-cols-12 gap-10 border-t-2 border-ink pt-7">
        <section data-testid="latest-block" className="lg:col-span-8">
          <h2 className="font-hindi text-2xl md:text-3xl mb-2">
            {t.latest}
            <span className="block gold-rule w-24 mt-2" />
          </h2>
          <div className="grid sm:grid-cols-2 gap-x-8">
            {latest.map((a) => (
              <NewsCard key={a.id} article={a} variant="small" />
            ))}
          </div>
        </section>
        <aside data-testid="trending-block" className="lg:col-span-4">
          <div className="border-2 border-ink bg-white">
            <h2 className="bg-deepgreen text-gold font-mono text-xs uppercase tracking-[0.25em] px-5 py-3">
              {t.trending}
            </h2>
            <ol className="p-5 list-none">
              {trending.map((a, i) => (
                <li key={a.id} className="flex gap-4 border-b border-subtle last:border-0 py-3">
                  <span className="font-serif text-3xl font-black text-gold/90 leading-none w-8 shrink-0">
                    {i + 1}
                  </span>
                  <Link
                    to={`/article/${a.id}`}
                    data-testid={`trending-${i}`}
                    className="font-hindi text-sm md:text-base leading-snug hover:text-deepgreen transition-colors"
                  >
                    {pickTitle(a, lang)}
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </Reveal>

      <AdSlot slot="middle" ads={ads} />

      {(data.special || []).length > 0 && (
        <Reveal>
          <section data-testid="special-strip">
            <h2 className="font-hindi text-2xl md:text-3xl mb-6">
              {lang === "hi" ? "विशेष प्रस्तुति" : "Special Features"}
              <span className="block gold-rule w-24 mt-2" />
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {take(data.special, 6).map((a) => (
                <div key={a.id} className="relative">
                  <NewsCard article={a} variant="standard" />
                  <span className="absolute top-2 left-2 bg-brk text-paper font-mono text-[9px] uppercase tracking-widest px-2 py-1">
                    {{ video: "वीडियो", gallery: "फोटो गैलरी", live: "लाइव", webstory: "वेब स्टोरी" }[a.kind] || a.kind}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </Reveal>
      )}

      {opinions.length > 0 && (
        <Reveal>
          <section data-testid="opinion-strip" className="bg-deepgreen text-paper p-6 md:p-10 border-y-4 border-gold">
            <h2 className="font-hindi text-2xl md:text-3xl text-gold-light mb-8">
              {t.opinion}
              <span className="block w-24 mt-2 h-[3px] bg-gold" />
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {opinions.map((a) => (
                <Link
                  key={a.id}
                  to={`/article/${a.id}`}
                  data-testid={`opinion-card-${a.id}`}
                  className="group block bg-paper text-ink p-5 border-l-4 border-gold hover:-translate-y-1 transition-transform duration-300"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brk">{t.opinion}</span>
                  <h3 className="font-hindi text-base md:text-lg leading-snug mt-2 group-hover:text-deepgreen transition-colors line-clamp-3">
                    {pickTitle(a, lang)}
                  </h3>
                  <span className="font-mono text-[11px] text-muted-foreground mt-3 block">{a.author}</span>
                </Link>
              ))}
            </div>
          </section>
        </Reveal>
      )}

      <SupportUs />

      {railOrder.map((id) => {
        const section = SECTIONS.find((s) => s.id === id);
        const arts = take(data.by_category[id], 5);
        return arts.length ? <SectionRail key={id} section={section} articles={arts} /> : null;
      })}

      <AdSlot slot="bottom" ads={ads} />
    </div>
  );
}
